import { useCallback, useEffect, useMemo, useState } from 'react';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Tooltip,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import type { QueryClient } from '@tanstack/react-query';
import { JsonFormsRenderer } from '@/components/JsonFormsRenderer';
import {
  useAllEntities,
  useCreateEntity,
  useEntityTypeWithSchemas,
  useUpdateEntity,
  useDeleteEntity,
} from '@/hooks';
import type { Entity } from '@/types/Entity';
import type { EntityActionNotifications } from '@/actions/entityActionRegistry';
import { queryKeys } from '@/lib/queryKeys';
import { logger } from '@equip/utils/logger';
import { httpClient } from '@/utils/httpClient';

const log = logger.child({ component: 'LocationActionDialogs' });

export type LocationActionDialogState =
  | {
      type: 'create';
      defaultParentId?: string | null;
      queryClient: QueryClient;
      notifications?: EntityActionNotifications;
    }
  | {
      type: 'edit';
      entity: Entity;
      queryClient: QueryClient;
      notifications?: EntityActionNotifications;
    }
  | {
      type: 'delete';
      entity: Entity;
      queryClient: QueryClient;
      notifications?: EntityActionNotifications;
    };

const normalizeLocationFormData = (data: Record<string, unknown>): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {
    ...data,
  };

  // Ensure metadata is removed as it is no longer supported
  delete normalized.metadata;

  const parentId = normalized.parent_location_id;
  normalized.parent_location_id =
    typeof parentId === 'string' && parentId.trim().length > 0 ? parentId : null;

  const locationType = normalized.location_type_id;
  normalized.location_type_id =
    typeof locationType === 'string' && locationType.trim().length > 0 ? locationType : null;

  if (typeof normalized.barcode === 'string') {
    normalized.barcode = normalized.barcode.trim() || null;
  }

  return normalized;
};

const useLocationFormState = (initialData: Record<string, unknown>, resetKey?: string) => {
  const [formData, setFormData] = useState<Record<string, unknown>>(initialData);
  const [isValid, setIsValid] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    setFormData(initialData);
    setIsValid(false);
    setHasAttemptedSubmit(false);
  }, [initialData, resetKey]);

  return {
    formData,
    setFormData,
    isValid,
    setIsValid,
    hasAttemptedSubmit,
    setHasAttemptedSubmit,
  } as const;
};

const useLocationSchemas = () => {
  const { data, isLoading, error } = useEntityTypeWithSchemas('location');
  return { config: data, isLoading, error } as const;
};

/** Parses structural metadata from location type entities. */
const useLocationTypes = () => {
  const { data } = useAllEntities({
    type: 'location_type',
    filters: { is_current: 'eq.true' },
  });

  return useMemo(() => {
    if (!data)
      return {
        types: [] as Array<{
          id: string;
          structuralType: string;
          structuralLevel: number | null;
          typeName: string;
          templateId: string | null;
        }>,
      };

    return {
      types: data.map((e) => {
        const ed = e.entity_data as Record<string, unknown> | undefined;
        return {
          id: e.id,
          structuralType: (ed?.structural_type as string) ?? '',
          structuralLevel: ed?.structural_level != null ? Number(ed.structural_level) : null,
          typeName: (ed?.type_name as string) ?? e.display_name ?? '',
          templateId: (ed?.template_id as string | null | undefined) ?? null,
        };
      }),
    };
  }, [data]);
};

/** Loads location hierarchy templates. */
const useLocationTemplates = () => {
  const { data } = useAllEntities({
    type: 'location_template',
    filters: { is_current: 'eq.true' },
  });
  return useMemo(
    () =>
      (data ?? []).map((e) => ({ id: e.id, label: e.display_name ?? e.entity_identifier ?? e.id })),
    [data],
  );
};

interface LocationFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  entity?: Entity;
  defaultParentId?: string | null;
  queryClient: QueryClient;
  notifications?: EntityActionNotifications;
  onClose: () => void;
}

const resettableClone = (source?: Record<string, unknown>): Record<string, unknown> =>
  source ? JSON.parse(JSON.stringify(source)) : {};

const buildIdentifier = () => `LOC-${Date.now()}`;

const invalidateLocationQueries = async (queryClient: QueryClient, entityId?: string) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.entities.list('location') }),
    queryClient.invalidateQueries({ queryKey: ['hierarchy'] }),
    entityId
      ? queryClient.invalidateQueries({ queryKey: queryKeys.entities.detail('location', entityId) })
      : Promise.resolve(),
  ]);
};

const LocationFormDialog = ({
  open,
  mode,
  entity,
  defaultParentId,
  queryClient,
  notifications,
  onClose,
}: LocationFormDialogProps) => {
  const { config, isLoading, error } = useLocationSchemas();
  const { types: locationTypes } = useLocationTypes();
  const locationTemplates = useLocationTemplates();

  /** Look up structural metadata for a given type UUID. */
  const getTypeInfo = useCallback(
    (typeId: string | null) => locationTypes.find((t) => t.id === typeId) ?? null,
    [locationTypes],
  );
  const createEntity = useCreateEntity();
  const updateEntity = useUpdateEntity();
  const isSubmitting = createEntity.isPending || updateEntity.isPending;

  useEffect(() => {
    if (!config || !import.meta.env.DEV) return;
    const schemaFields = Object.keys(config.entity_schema?.properties ?? {});
    log.debug('Config snapshot', {
      hasUiSchema: Boolean(config.ui_schema),
      schemaFields,
      uiSchemaSnippet: config.ui_schema,
    });
  }, [config]);

  const initialFormData = useMemo(() => {
    if (mode === 'edit' && entity) {
      const base = resettableClone(entity.entity_data);
      delete base.metadata;
      return base;
    }
    const base: Record<string, unknown> = {
      parent_location_id: defaultParentId ?? null,
    };
    return base;
  }, [mode, entity, defaultParentId]);

  const resetKey = `${mode}-${entity?.id ?? 'new'}-${defaultParentId ?? 'root'}-${open ? 'open' : 'closed'}`;

  const { formData, setFormData, isValid, setIsValid, hasAttemptedSubmit, setHasAttemptedSubmit } =
    useLocationFormState(initialFormData, resetKey);

  // Derive the currently-selected template from formData or from the selected type
  const derivedTemplateId = useMemo(() => {
    const direct =
      typeof formData.template_id === 'string' && formData.template_id.length > 0
        ? formData.template_id
        : null;
    if (direct) return direct;
    const typeId = typeof formData.location_type_id === 'string' ? formData.location_type_id : null;
    return locationTypes.find((t) => t.id === typeId)?.templateId ?? null;
  }, [formData.template_id, formData.location_type_id, locationTypes]);

  const selectedTemplateOption = useMemo(
    () => locationTemplates.find((t) => t.id === derivedTemplateId) ?? null,
    [locationTemplates, derivedTemplateId],
  );

  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);

  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleValidationChange = (valid: boolean) => {
    setIsValid(valid);
  };

  const handleFormDataChange = (next: Record<string, unknown>) => {
    const newTypeId =
      typeof next.location_type_id === 'string' && next.location_type_id.length > 0
        ? next.location_type_id
        : null;
    const prevTypeId =
      typeof formData.location_type_id === 'string' && formData.location_type_id.length > 0
        ? formData.location_type_id
        : null;

    // When the type changes, clear parent for root types and auto-set template_id
    if (newTypeId !== prevTypeId && newTypeId) {
      const info = getTypeInfo(newTypeId);
      if (info?.structuralType === 'root') {
        next = { ...next, parent_location_id: null };
      }
      if (info?.templateId) {
        next = { ...next, template_id: info.templateId };
      }
    }

    setFormData(next ?? {});
  };

  const handleTemplateChange = useCallback(
    (templateId: string | null) => {
      // If the currently-selected location type belongs to a different template, clear it
      const currentTypeId =
        typeof formData.location_type_id === 'string' ? formData.location_type_id : null;
      const currentTypeTemplate =
        locationTypes.find((t) => t.id === currentTypeId)?.templateId ?? null;
      const shouldClearType = currentTypeId !== null && currentTypeTemplate !== templateId;
      setFormData((prev) => ({
        ...prev,
        template_id: templateId ?? null,
        ...(shouldClearType ? { location_type_id: null, parent_location_id: null } : {}),
      }));
    },
    [formData.location_type_id, locationTypes],
  );

  const isEditMode = mode === 'edit' && !!entity;

  // Parent Location is required for all location types except Root
  const selectedTypeId =
    typeof formData.location_type_id === 'string' && formData.location_type_id.length > 0
      ? formData.location_type_id
      : null;

  // Determine which parent structural_type values to show based on selected type
  const parentTypeFilter = useMemo(() => {
    if (!selectedTypeId) return null;
    const info = getTypeInfo(selectedTypeId);
    if (!info) return null;

    if (info.structuralType === 'root') return null; // Root has no parent

    if (info.structuralType === 'level') {
      if (info.structuralLevel === 1) {
        // Level 1 → parent must be root
        return 'eq.root';
      }
      // Level N → parent must be level N-1
      return 'eq.level';
    }

    return null;
  }, [selectedTypeId, getTypeInfo]);

  const parentLevelFilter = useMemo(() => {
    if (!selectedTypeId) return null;
    const info = getTypeInfo(selectedTypeId);
    if (!info) return null;
    if (info.structuralType !== 'level') return null;
    if (info.structuralLevel == null || info.structuralLevel <= 1) return null;
    return `eq.${info.structuralLevel - 1}`;
  }, [selectedTypeId, getTypeInfo]);

  const effectiveUiSchema = useMemo(() => {
    const baseSchema = isEditMode
      ? (config?.ui_schema_edit ?? config?.ui_schema)
      : config?.ui_schema;
    if (!baseSchema) return undefined;

    // Deep clone to safely mutate dropdown params
    const schema = JSON.parse(JSON.stringify(baseSchema));

    // Determine which types belong to the selected template
    const templateTypes = derivedTemplateId
      ? locationTypes.filter((t) => t.templateId === derivedTemplateId)
      : locationTypes;

    // Non-root type IDs within the template (only these can have a parent)
    const nonRootTypeIds = templateTypes
      .filter((t) => t.structuralType !== 'root')
      .map((t) => t.id);

    // If the template has NO level types (e.g. Kiosk = root only), remove parent field entirely
    const templateHasLevelTypes = templateTypes.some((t) => t.structuralType === 'level');

    const processElements = (elements: unknown[]): unknown[] => {
      return elements.flatMap((el) => {
        const element = el as Record<string, unknown>;

        // Recurse into groups
        if (Array.isArray(element.elements)) {
          return [{ ...element, elements: processElements(element.elements as unknown[]) }];
        }

        // location_type_id: filter to types in the selected template
        if (element.scope === '#/properties/location_type_id') {
          const opts = (element.options ?? {}) as Record<string, unknown>;
          const ds = (opts.dataSource ?? {}) as Record<string, unknown>;
          const params = { ...(ds.params as Record<string, string> | undefined) } as Record<
            string,
            string
          >;
          if (derivedTemplateId) {
            params['entity_data->>template_id'] = `eq.${derivedTemplateId}`;
          } else {
            delete params['entity_data->>template_id'];
          }
          return [{ ...element, options: { ...opts, dataSource: { ...ds, params } } }];
        }

        // parent_location_id: remove entirely for root-only templates,
        // otherwise hide until a non-root type is selected
        if (element.scope === '#/properties/parent_location_id') {
          if (!templateHasLevelTypes) {
            return []; // Remove field — template has only root types (e.g. Kiosk)
          }

          const opts = (element.options ?? {}) as Record<string, unknown>;
          const ds = (opts.dataSource ?? {}) as Record<string, unknown>;
          const params = { ...(ds.params as Record<string, string> | undefined) } as Record<
            string,
            string
          >;
          if (parentTypeFilter) {
            params.structural_type = parentTypeFilter;
          } else {
            delete params.structural_type;
          }
          if (parentLevelFilter) {
            params.structural_level = parentLevelFilter;
          } else {
            delete params.structural_level;
          }
          if (derivedTemplateId) {
            params.type_template_id = `eq.${derivedTemplateId}`;
          } else {
            delete params.type_template_id;
          }

          // Hide unless selected location_type_id is a non-root type
          const hideRule =
            nonRootTypeIds.length > 0
              ? {
                  effect: 'HIDE',
                  condition: {
                    scope: '#/properties/location_type_id',
                    schema: { not: { enum: nonRootTypeIds } },
                  },
                }
              : undefined;

          return [
            {
              ...element,
              options: { ...opts, dataSource: { ...ds, params } },
              ...(hideRule ? { rule: hideRule } : {}),
            },
          ];
        }

        return [element];
      });
    };

    if (Array.isArray(schema.elements)) {
      schema.elements = processElements(schema.elements as unknown[]);
    }

    return schema;
  }, [
    config?.ui_schema,
    config?.ui_schema_edit,
    isEditMode,
    parentTypeFilter,
    parentLevelFilter,
    derivedTemplateId,
    locationTypes,
  ]);

  const effectiveSchema = config?.entity_schema;

  const dialogTitle = mode === 'edit' ? 'Edit Location' : 'Create Location';
  const selectedTypeInfo = getTypeInfo(selectedTypeId);
  const isParentRequired = selectedTypeId !== null && selectedTypeInfo?.structuralType !== 'root';
  const isParentMissing =
    isParentRequired &&
    (typeof formData.parent_location_id !== 'string' ||
      formData.parent_location_id.trim().length === 0);

  const isTemplateMissing = !derivedTemplateId;

  const saveTooltip = useMemo(() => {
    if (isSubmitting || (isValid && !isParentMissing && !isTemplateMissing)) return '';
    const missing: string[] = [];
    if (!formData.name) missing.push('Location Name');
    if (isTemplateMissing) missing.push('Hierarchy Template');
    if (!formData.location_type_id) missing.push('Location Type');
    if (isParentMissing) missing.push('Parent Location');
    if (missing.length > 0) return `Required: ${missing.join(', ')}`;
    return 'Please fix validation errors before saving';
  }, [
    isValid,
    isSubmitting,
    isParentMissing,
    isTemplateMissing,
    formData.name,
    formData.location_type_id,
  ]);

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);
    if (!isValid || isSubmitting) {
      notifications?.error('Validation error', 'Please resolve the highlighted fields.');
      return;
    }
    if (isTemplateMissing) {
      notifications?.error('Validation error', 'A Hierarchy Template is required.');
      return;
    }
    if (isParentMissing) {
      notifications?.error(
        'Validation error',
        'Parent Location is required for this location type.',
      );
      return;
    }

    try {
      if (mode === 'edit' && entity) {
        const displayName =
          typeof formData.name === 'string' && formData.name.length > 0
            ? String(formData.name)
            : entity.display_name;
        const normalized = normalizeLocationFormData(formData);
        await updateEntity.mutateAsync({
          type: 'location',
          id: entity.id,
          patch: {
            entity_data: normalized,
            display_name: displayName,
          },
        });
        notifications?.success('Location updated', `${displayName} saved successfully.`);
        await invalidateLocationQueries(queryClient, entity.id);
      } else {
        const name =
          typeof formData.name === 'string' && formData.name.length > 0
            ? String(formData.name)
            : 'New Location';
        const normalized = normalizeLocationFormData({
          ...formData,
          template_id: derivedTemplateId ?? formData.template_id ?? null,
        });
        await createEntity.mutateAsync({
          entity_type: 'location',
          entity_identifier: buildIdentifier(),
          display_name: name,
          entity_data: normalized,
          status: 'active',
        });
        notifications?.success('Location created', `${name} added successfully.`);
        await invalidateLocationQueries(queryClient);
      }
      onClose();
    } catch (submitError) {
      notifications?.error(
        'Action failed',
        submitError instanceof Error ? submitError.message : 'Unable to save location.',
      );
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleRequestClose} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {error && <Alert severity="error">Failed to load form schema.</Alert>}
          {/* Template selector — required for all locations */}
          <Autocomplete
            options={locationTemplates}
            getOptionLabel={(o) => o.label}
            value={selectedTemplateOption}
            onChange={(_, v) => handleTemplateChange(v?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Hierarchy Template"
                required
                error={hasAttemptedSubmit && isTemplateMissing}
                helperText={
                  hasAttemptedSubmit && isTemplateMissing
                    ? 'A hierarchy template is required'
                    : "Select the template that governs this location's type hierarchy"
                }
                size="small"
                sx={{ mb: 2 }}
              />
            )}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disabled={isSubmitting}
            sx={{ mt: 1 }}
          />
          {isParentMissing && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Parent Location is required.
            </Alert>
          )}
          {!isLoading && !error && effectiveSchema && (
            <>
              {isTemplateMissing && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select a Hierarchy Template above to fill out location details.
                </Alert>
              )}
              <JsonFormsRenderer
                schema={effectiveSchema}
                uiSchema={effectiveUiSchema}
                data={formData}
                onChange={handleFormDataChange}
                onValidationChange={handleValidationChange}
                validationMode={hasAttemptedSubmit ? 'ValidateAndShow' : 'ValidateAndHide'}
                readonly={isTemplateMissing}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRequestClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Tooltip title={saveTooltip} arrow>
            <span>
              <ViewerGuardedButton
                onClick={() => void handleSubmit()}
                variant="contained"
                disabled={!isValid || isSubmitting || isParentMissing || isTemplateMissing}
              >
                {isSubmitting ? 'Saving…' : 'Save'}
              </ViewerGuardedButton>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        aria-labelledby="discard-location-changes-dialog-title"
      >
        <DialogTitle id="discard-location-changes-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to discard them?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscardOpen(false)}>Keep Editing</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmDiscardOpen(false);
              onClose();
            }}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export interface LocationActionDialogsProps {
  state: LocationActionDialogState | null;
  onClose: () => void;
}

interface LocationDeleteDialogProps {
  open: boolean;
  entity: Entity;
  queryClient: QueryClient;
  notifications?: EntityActionNotifications;
  onClose: () => void;
}

const LocationDeleteDialog = ({
  open,
  entity,
  queryClient,
  notifications,
  onClose,
}: LocationDeleteDialogProps) => {
  const deleteEntity = useDeleteEntity();
  const [assetCount, setAssetCount] = useState<number | null>(null);
  const [checkLoading, setCheckLoading] = useState(true);
  const [checkError, setCheckError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCheckLoading(true);
    setCheckError(false);
    setAssetCount(null);

    httpClient
      .get<Array<{ stock_quantity: number }>>(
        `/api/rest/data/v1/asset_inventory_summary?location_id=eq.${entity.id}&select=stock_quantity`,
      )
      .then((rows) => {
        const total = rows.reduce((sum, r) => sum + (r.stock_quantity ?? 0), 0);
        setAssetCount(total);
      })
      .catch(() => {
        setCheckError(true);
      })
      .finally(() => {
        setCheckLoading(false);
      });
  }, [open, entity.id]);

  const handleConfirmDelete = async () => {
    try {
      await deleteEntity.mutateAsync({ id: entity.id, type: 'location' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.entities.list('location') }),
        queryClient.invalidateQueries({ queryKey: ['hierarchy'] }),
      ]);
      notifications?.success('Location deleted', `"${entity.display_name}" has been deleted.`);
      onClose();
    } catch (err) {
      notifications?.error(
        'Delete failed',
        err instanceof Error ? err.message : 'Unable to delete location.',
      );
    }
  };

  const locationName = entity.display_name ?? entity.entity_identifier ?? 'this location';
  const hasAssets = assetCount !== null && assetCount > 0;

  return (
    <Dialog
      open={open}
      onClose={deleteEntity.isPending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="location-delete-title"
      aria-describedby="location-delete-description"
    >
      <DialogTitle
        id="location-delete-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <WarningIcon color="warning" />
        Delete Location
      </DialogTitle>
      <DialogContent>
        {checkLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        {checkError && (
          <Alert severity="error">Unable to verify asset assignments. Please try again.</Alert>
        )}
        {!checkLoading && !checkError && hasAssets && (
          <DialogContentText id="location-delete-description">
            <strong>{locationName}</strong> has {assetCount} asset
            {assetCount !== 1 ? 's' : ''} assigned to it. All assets must be moved to another
            location before this location can be deleted.
          </DialogContentText>
        )}
        {!checkLoading && !checkError && !hasAssets && (
          <DialogContentText id="location-delete-description">
            Are you sure you want to delete <strong>{locationName}</strong>? This action cannot be
            undone.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleteEntity.isPending}>
          {hasAssets ? 'Close' : 'Cancel'}
        </Button>
        {!checkLoading && !checkError && !hasAssets && (
          <ViewerGuardedButton
            onClick={() => void handleConfirmDelete()}
            variant="contained"
            color="error"
            disabled={deleteEntity.isPending}
          >
            {deleteEntity.isPending ? 'Deleting…' : 'Delete'}
          </ViewerGuardedButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const LocationActionDialogs = ({ state, onClose }: LocationActionDialogsProps) => {
  if (!state) {
    return null;
  }

  if (state.type === 'create') {
    return (
      <LocationFormDialog
        open
        mode="create"
        defaultParentId={state.defaultParentId ?? null}
        queryClient={state.queryClient}
        notifications={state.notifications}
        onClose={onClose}
      />
    );
  }

  if (state.type === 'edit') {
    return (
      <LocationFormDialog
        open
        mode="edit"
        entity={state.entity}
        queryClient={state.queryClient}
        notifications={state.notifications}
        onClose={onClose}
      />
    );
  }

  if (state.type === 'delete') {
    return (
      <LocationDeleteDialog
        open
        entity={state.entity}
        queryClient={state.queryClient}
        notifications={state.notifications}
        onClose={onClose}
      />
    );
  }

  return null;
};
